import React, { useState, useRef, useLayoutEffect, useMemo } from "react";
import AiSummaryIcon from "../../assets/svg/ai-summary-icon";
// import VerbafloLogoFullIcon from "../../assets/svg/verbaflo-logo-full-icon";
import VerbafloLogoIcon from "../../assets/images/verbaflo-logo.png";
import { useSearchHighlight } from "../../context/SearchHighlightContext";
import { highlightTextInPlain } from "../../pages/Communications/utils/highlightSearchText";

interface AISummaryBlockProps {
  text: string;
  messageId?: string;
  title?: string;
  showLogo?: boolean;
  maxLines?: number;
  className?: string;
  isSent?: boolean;
}

const AISummaryBlock = ({
  text,
  messageId,
  title = "AI Summary",
  showLogo = true,
  maxLines = 2,
  isSent = false,
  className = "",
}: AISummaryBlockProps) => {
  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const [clampedHeight, setClampedHeight] = useState(0);
  const [fullHeight, setFullHeight] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const fullTextRef = useRef<HTMLParagraphElement>(null);

  const { searchQuery } = useSearchHighlight();

  const highlightedText = useMemo(() => {
    if (!text) return "";
    if (searchQuery && searchQuery.trim() !== "") {
      return highlightTextInPlain(text, searchQuery);
    }
    return text;
  }, [text, searchQuery]);

  useLayoutEffect(() => {
    if (textRef.current && fullTextRef.current) {
      const computedStyle = getComputedStyle(textRef.current);
      let lineHeight = parseFloat(computedStyle.lineHeight);
      if (isNaN(lineHeight)) {
        const fontSize = parseFloat(computedStyle.fontSize);
        lineHeight = fontSize * 1.2;
      }
      const collapseLineHeight = lineHeight * maxLines;

      setClampedHeight(collapseLineHeight);
      setFullHeight(fullTextRef.current.scrollHeight);

      if (fullTextRef.current.scrollHeight > collapseLineHeight + 1) {
        setShowToggle(true);
      } else {
        setShowToggle(false);
      }
    }
  }, [text, maxLines]);

  const handleToggle = () => {
    setIsAnimating(true);
    setExpanded((prev) => !prev);

    setTimeout(() => {
      setIsAnimating(false);
    }, 200);
  };

  return (
    <div
      className={`bg-gradient-ai-card flex flex-col gap-[10px] px-[12px] py-[10px] rounded-[8px] w-[500px] ${isSent ? "ml-auto" : "mr-auto"
        } ${className} message-card`}
      data-message-id={messageId}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[4px]">
          <AiSummaryIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue" />
          <p className="text-heading-text dark:text-heading-text-dk font-medium text-md">
            {title}
          </p>
        </div>

        {showLogo && (
          // <VerbafloLogoFullIcon className="w-[70px] h-[20px] text-verba-indigo dark:text-verba-indigo-dk" />
          <div className="flex items-center gap-[2px]">
            <img
              src={VerbafloLogoIcon}
              alt="Verbaflo Logo"
              className="w-[18px] h-[18px]"
            />
            <p className="text-verba-indigo dark:text-verba-indigo-dk text-xs font-bold">
              VerbaFlo.
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-col items-start gap-[10px] text-heading-text dark:text-heading-text-dk text-md font-normal">
        <div className="relative w-full">
          <div
            className="overflow-hidden transition-all duration-200 ease-in-out"
            style={{
              maxHeight: expanded ? `${fullHeight}px` : `${clampedHeight}px`,
            }}
          >
            <p
              ref={textRef}
              className={`${expanded || isAnimating
                ? "line-clamp-none"
                : `line-clamp-${maxLines}`
                }`}
              dangerouslySetInnerHTML={{ __html: highlightedText }}
            />
          </div>

          <p
            ref={fullTextRef}
            className="absolute top-0 left-0 w-full opacity-0 pointer-events-none"
            style={{ lineHeight: "inherit" }}
          >
            {text}
          </p>
        </div>

        {showToggle && (
          <button
            type="button"
            className="shrink-0 text-verba-blue-dark-mode dark:text-verba-blue-dark-mode-dk text-md !font-normal transition-colors text-md hover:underline"
            onClick={handleToggle}
          >
            {expanded ? "Read Less" : "Read More"}
          </button>
        )}
      </div>
    </div>
  );
};

export default AISummaryBlock;
